import type { KnowledgeBaseInfo } from '@shared/api';
import { Table } from '@shared/ui/table';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Flex } from 'antd';
import { DateService } from '@shared/services/date';
import { Settings } from './ui/settings/settings';

interface KnowledgeBaseTableProps {
  knowledgeBaseInfo?: KnowledgeBaseInfo[];
  selectedKnowledgeBaseIds: number[];
  onToggleKnowledgeBaseSelect: (knowledgeBaseId: number, checked: boolean) => void;
  onToggleAllKnowledgeBaseSelect: (checked: boolean) => void;
  onDeleteKnowledgeBase: (knowledgeBaseId: number) => void;
  onDownloadKnowledgeBase: (knowledgeBaseId: number) => void;
  isBulkDeleting?: boolean;
}

export const KnowledgeBaseTable: FC<KnowledgeBaseTableProps> = ({
  knowledgeBaseInfo,
  selectedKnowledgeBaseIds,
  onToggleKnowledgeBaseSelect,
  onToggleAllKnowledgeBaseSelect,
  onDeleteKnowledgeBase,
  onDownloadKnowledgeBase,
  isBulkDeleting = false,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase.table' });
  const selectedKnowledgeBaseIdsSet = new Set(selectedKnowledgeBaseIds);
  const totalItems = knowledgeBaseInfo?.length ?? 0;
  const selectedItemsCount = selectedKnowledgeBaseIds.length;
  const isAllSelected = totalItems > 0 && selectedItemsCount === totalItems;
  const isPartiallySelected = selectedItemsCount > 0 && selectedItemsCount < totalItems;

  const statusConfig: Record<KnowledgeBaseInfo['status'], { label: string; color: string }> = {
    LOADING: { label: t('status.loading'), color: '#FAAD14' },
    LOADED: { label: t('status.loaded'), color: '#52C41A' },
    ERROR: { label: t('status.error'), color: '#FF4D4F' },
  };
  const dataTypeLabels: Record<KnowledgeBaseInfo['data_type'], string> = {
    file: t('dataTypes.file'),
    link: t('dataTypes.link'),
    text: t('dataTypes.text'),
    linked_document: t('dataTypes.linkedDocument'),
  };

  return (
    <Table maxHeight={`calc(100vh - 200px)`}>
      <Table.THead isSticky>
        <Table.THeadCellText width={52}>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            disabled={!totalItems || isBulkDeleting}
            onChange={(event) => onToggleAllKnowledgeBaseSelect(event.target.checked)}
          />
        </Table.THeadCellText>
        <Table.THeadCellText width={150}>{t('title')}</Table.THeadCellText>
        <Table.THeadCellText width={200}>{t('description')}</Table.THeadCellText>
        <Table.THeadCellText width={150}>{t('type')}</Table.THeadCellText>
        <Table.THeadCellText width={200}>{t('createdAt')}</Table.THeadCellText>
        <Table.THeadCellText width={140}>{t('status.title')}</Table.THeadCellText>
        <Table.THeadCellText
          width={100}
          style={{ textAlign: 'center' }}
          textStyle={{ textAlign: 'center' }}
        >
          <></>
        </Table.THeadCellText>
      </Table.THead>
      <Table.TBody>
        {knowledgeBaseInfo &&
          knowledgeBaseInfo.map((knowledgeBase) => {
            const statusMeta = statusConfig[knowledgeBase.status] ?? {
              label: knowledgeBase.status ?? '-',
              color: '#8C8C8C',
            };

            return (
              <Table.TRow key={knowledgeBase.id}>
                <Table.TCell width={52}>
                  <Checkbox
                    checked={selectedKnowledgeBaseIdsSet.has(knowledgeBase.id)}
                    disabled={isBulkDeleting}
                    onChange={(event) =>
                      onToggleKnowledgeBaseSelect(knowledgeBase.id, event.target.checked)
                    }
                  />
                </Table.TCell>
                <Table.TCellTextTooltip maxLength={20} variant="sm" text={knowledgeBase.title} />
                <Table.TCellTextTooltip
                  maxLength={30}
                  variant="sm"
                  text={knowledgeBase.description || '-'}
                />

                <Table.TCellText variant="sm" fontWeight={400}>
                  {dataTypeLabels[knowledgeBase.data_type] ?? knowledgeBase.data_type}
                </Table.TCellText>

                <Table.TCellText variant="sm" fontWeight={400}>
                  {DateService.format(knowledgeBase.created_at, 'DD.MM.YYYY HH:mm')}
                </Table.TCellText>

                <Table.TCellText
                  variant="sm"
                  fontWeight={400}
                  textStyle={{ color: statusMeta.color }}
                >
                  {statusMeta.label}
                </Table.TCellText>

                <Table.TCell key="actions" align="center" style={{ padding: 0 }}>
                  <Flex align="center" justify="center">
                    <Settings
                      knowledgeBaseId={knowledgeBase.id}
                      dataType={knowledgeBase.data_type}
                      syncPeriod={knowledgeBase.sync_period}
                      onDeleteKnowledgeBase={() => onDeleteKnowledgeBase(knowledgeBase.id)}
                      onDownloadKnowledgeBase={() => onDownloadKnowledgeBase(knowledgeBase.id)}
                      disabled={isBulkDeleting}
                    />
                  </Flex>
                </Table.TCell>
              </Table.TRow>
            );
          })}
      </Table.TBody>
    </Table>
  );
};
